/*
Splash tips loader — mirrors Android SplashTips.kt.

The /data/splash-tips.json asset is produced by tools/build_dataset.py
and has the shape:

  {
    "version": 1,
    "intro": ["intro.welcome", "intro.dataset_size", ...],   // 10 ordered ids
    "tips":  [{ "id": "...", "category": "...", "text": "..." }, ...]
  }

`intro` carries the tip ids shown (in order) on the very first launch's
splash; the runtime rotates through them once, then the splashIntroSeen
flag flips and subsequent splashes pick at random from `tips`.

Tip text may contain placeholders like `{captureCount}`,
`{speciesCount}`, `{remarquableCount}`, `{daysSinceFirst}` that are
substituted from a per-session player snapshot. A tip whose
substitution yields a missing value is skipped at selection time.
*/

import { fetchJson } from './fetchJson'

const kAssetPath = '/data/splash-tips.json'  // canonical SW-cached asset path

export interface Tip
{
	id: string
	category: string
	text: string
}

interface RawFile
{
	version: number
	intro: string[]
	tips: Tip[]
}

let fIntroTips: Tip[] | null = null  // memoized intro list, in catalogue order
let fAllTips: Tip[] | null = null  // memoized full bank for random rotation

/*
Ensure splash tips are loaded. Idempotent. Throws via fetchJson on bad
JSON or HTTP failure; callers degrade gracefully (skip splash UX).
*/
export async function ensureSplashTips(): Promise<void>
{
	if (fIntroTips && fAllTips) return
	const vRaw = await fetchJson<RawFile>(kAssetPath)
	const vById = new Map(vRaw.tips.map((inT) => [inT.id, inT]))  // tip-id -> tip lookup, for the intro id resolution
	fIntroTips = vRaw.intro.map((inId) => vById.get(inId)).filter((inT): inT is Tip => Boolean(inT))
	fAllTips = vRaw.tips
}

export function getIntroTips(): Tip[] { return fIntroTips ?? [] }
export function getAllTips(): Tip[] { return fAllTips ?? [] }
